import { motion } from "framer-motion";
import { Heart, Lightbulb, Sun, Blocks } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { CTABanner } from "../components/CTABanner";
import { SEO } from "../components/SEO";

const spring = { type: "spring", stiffness: 200, damping: 20 };

const VALUES = [
  {
    icon: Heart,
    color: "bg-sunny text-navy",
    title: "Patient-centered care",
    text: "We commit to delivering specialized, age-appropriate treatment plans that will bring out the best in your child.",
  },
  {
    icon: Sun,
    color: "bg-teal text-navy",
    title: "Healing",
    text: "We care for the well-being of the whole individual, not just the diagnosis. We vow to respect and pay attention to all needs of the child; physical, emotional, and social.",
  },
  {
    icon: Lightbulb,
    color: "bg-lilac text-navy",
    title: "Integrity",
    text: "We pledge to adhere to the highest level of professionalism, confidentiality, and evidence-based care.",
  },
  {
    icon: Blocks,
    color: "bg-butter text-navy",
    title: "Purposeful play",
    text: "Children learn best when they're having fun. Every session is packed with purposeful play so therapy never feels like work.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Us | Thrive Pediatric Therapy, Nassau, Bahamas"
        description="Thrive is a proudly Bahamian, multidisciplinary pediatric therapy practice in Nassau, founded by pediatric physiotherapist Holly Sands in 2022. Physiotherapy, occupational therapy, speech & language and feeding therapy under one roof."
      />
      <PageHeader
        testId="about-header"
        title="Empowered. Equipped. Encouraged. Thriving."
        description="Pediatric Physiotherapy, Occupational Therapy and Speech Therapy services under one roof! We're living our mission and truly embodying what it means to thrive."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={spring}>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-navy tracking-tight">Our story</h2>
            <div data-testid="about-story" className="mt-6 space-y-5 text-base text-navy-light leading-relaxed">
              <p>
                Thrive was founded by pediatric physiotherapist Holly Sands from a simple belief: every child deserves the opportunity and support to thrive. Upon completion of her studies and returning home to The Bahamas, Holly recognized a gap in dedicated pediatric physiotherapy services and an opportunity to build the kind of practice she had long envisioned. In October 2022, Thrive opened its doors as a private pediatric physiotherapy practice created intentionally for children and their families.
              </p>
              <p>
                From the beginning, however, the vision was always bigger than physiotherapy alone. Children rarely develop in neat, isolated categories. Their needs often overlap, and the best care considers the whole child. Occupational Therapy joined Thrive in 2024, followed by Speech and Language Therapy in 2026, bringing to life the multidisciplinary practice Holly had envisioned from the start.
              </p>
              <p>
                At the heart of Thrive is a commitment that remains unchanged: every family that enters our doors should leave feeling more empowered, equipped, encouraged and educated. Whether we are helping a child reach a new milestone, build independence, communicate more confidently or participate more fully in everyday life, our goal is to provide families with expert guidance, practical tools and a team that truly understands their child.
              </p>
              <p>
                Today, Thrive continues to grow alongside the children and families it serves: proudly Bahamian, intentionally multidisciplinary, and committed to helping raise the standard of pediatric allied healthcare in The Bahamas. Our goal is not simply to provide therapy, but to embody what it means to thrive.
              </p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ ...spring, delay: 0.1 }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-teal-soft blob-shape opacity-60 pointer-events-none" />
            <img
              src="/thrive-about.jpg"
              alt="A Thrive therapist helping a toddler practice standing and walking"
              data-testid="about-story-image"
              className="relative blob-shape-alt w-full max-w-md mx-auto aspect-[4/5] object-cover object-[center_20%] shadow-xl shadow-navy/10"
              loading="lazy"
            />
          </motion.div>
        </div>
      </section>

      <section data-testid="about-values" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={spring} className="max-w-2xl">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-navy tracking-tight">Our Mission</h2>
          <p className="mt-4 text-base sm:text-lg text-navy-light leading-relaxed">Our mission is to help every child thrive through compassionate, individualized, and age-appropriate pediatric care. We meet each child where they are and partner closely with families to build meaningful skills for everyday life. In everything we do, we strive to empower children and families and truly embody what it means to thrive.</p>
        </motion.div>
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={spring}
          data-testid="about-values-heading"
          className="mt-12 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-navy tracking-tight"
        >
          Core Values
        </motion.h3>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((value, i) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ ...spring, delay: i * 0.08 }}
              data-testid={`about-value-${i}`}
              className="bg-white rounded-[2rem] border border-border p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-navy/5"
            >
              <span className={`w-13 h-13 inline-flex w-12 h-12 rounded-2xl ${value.color} items-center justify-center`}>
                <value.icon className="w-6 h-6" strokeWidth={2.25} />
              </span>
              <h3 className="mt-5 font-display text-xl font-semibold text-navy">{value.title}</h3>
              <p className="mt-2.5 text-sm text-navy-light leading-relaxed">{value.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <CTABanner title="Come see the Thrive difference" subtitle="Book an initial evaluation and discover how joyful therapy can be." />
    </div>
  );
}
